/**
 * Configuration for Finland Location Hub with ALL supported cities from MarketHeader
 * Includes all 24 locations from LOCATIONS_WITH_BACKGROUNDS constant
 */

import type { CityCluster, CityData, SupportedLocale } from '../types/location-hub';
import { toLocationSlug } from './to-location-slug';

export const FINLAND_CITY_CLUSTERS: CityCluster[] = [
  {
    id: 'metropolitan',
    isPrimary: true,
    title: {
      en: 'Metropolitan Area',
      fi: 'Pääkaupunkiseutu',
      sv: 'Huvudstadsregionen',
    },
    description: {
      en: 'Finland\'s primary economic engine, offering the highest density of English-speaking and remote roles with excellent transport connectivity (HSL).',
      fi: 'Suomen talouden moottori: eniten englanninkielisiä työpaikkoja ja etätöitä, ja joukkoliikenne (HSL) toimii hyvin.',
      sv: 'Finlands primära ekonomiska motor, som erbjuder högsta tätheten av engelskspråkiga och distansroller med utmärkt transportanslutning (HSL).',
    },
    cities: [
      {
        slug: 'Helsinki_Finland',
        name: { en: 'Helsinki', fi: 'Helsinki', sv: 'Helsingfors' },
        specialty: { en: 'AI & Finance', fi: 'AI ja rahoitus', sv: 'AI och finans' },
        icon: 'Tower',
        narrative: {
          en: 'Finland\'s capital and tech hub, home to multinational corporations (MNCs) and a thriving startup ecosystem.',
          fi: 'Suomen pääkaupunki ja teknologian keskus: monikansallisia yrityksiä ja vilkas startup-kenttä.',
          sv: 'Finlands huvudstad och tekniska nav, hem för multinationella företag (MNC) och ett blomstrande startup-ekosystem.',
        },
      },
      {
        slug: 'Espoo_Finland',
        name: { en: 'Espoo', fi: 'Espoo', sv: 'Esbo' },
        specialty: { en: 'Tech Innovation', fi: 'Teknologinen innovaatio', sv: 'Teknisk innovation' },
        icon: 'Tech',
        narrative: {
          en: 'Innovation capital with Otaniemi campus, major tech companies, and strong R&D focus.',
          fi: 'Otaniemen kampus, suuret teknologiayritykset ja paljon tuotekehitystä tekevät Espoosta innovaatioiden keskuksen.',
          sv: 'Innovationshuvudstad med Otnäs campus, stora teknikföretag och stark FoU-inriktning.',
        },
      },
      {
        slug: 'Vantaa_Finland',
        name: { en: 'Vantaa', fi: 'Vantaa', sv: 'Vanda' },
        specialty: { en: 'Aviation Hub', fi: 'Ilmailukeskus', sv: 'Flygnav' },
        icon: 'Airport',
        narrative: {
          en: 'Aviation and logistics hub with Helsinki Airport, supporting international business and cargo operations.',
          fi: 'Ilmailun ja logistiikan keskus Helsinki-Vantaan lentoaseman ympärillä: kansainvälistä liiketoimintaa ja rahtia.',
          sv: 'Flyg- och logistiknav med Helsingfors-Vanda flygplats, stöder internationell verksamhet och fraktoperationer.',
        },
      },
    ],
  },
  {
    id: 'westCoast',
    title: { en: 'West Coast', fi: 'Länsirannikko', sv: 'Västkusten' },
    description: {
      en: 'Energy transition and maritime exports characterize the western coastal cities, with strong industrial heritage.',
      fi: 'Länsirannikon kaupungeilla on vahva teollinen perinne, ja nyt niitä leimaavat energiamurros ja merenkulun vienti.',
      sv: 'Energiövergång och maritim export kännetecknar de västra kuststäderna med starkt industrisarv.',
    },
    cities: [
      {
        slug: 'Turku_Finland',
        name: { en: 'Turku', fi: 'Turku', sv: 'Åbo' },
        specialty: { en: 'Energy & Maritime', fi: 'Energia ja merenkulku', sv: 'Energi och sjöfart' },
        icon: 'Ship',
        narrative: {
          en: 'Energy transition and maritime exports in Turku/Naantali, with strong biotech and shipbuilding industries.',
          fi: 'Energiamurros ja merenkulun vienti Turussa ja Naantalissa. Vahvoja aloja ovat bioteknologia ja laivanrakennus.',
          sv: 'Energiövergång och maritim export i Åbo/Nådendal med stark bioteknik- och skeppsbyggnadsindustri.',
        },
      },
      {
        slug: 'Pori_Finland',
        name: { en: 'Pori', fi: 'Pori', sv: 'Björneborg' },
        specialty: { en: 'Port & Energy', fi: 'Satama ja energia', sv: 'Hamn och energi' },
        icon: 'Ship',
        narrative: {
          en: 'Vibrant coastal city known for its port, energy sector, and the famous Pori Jazz Festival.',
          fi: 'Vireä rannikkokaupunki, jonka tunnetuimpia ovat satama, energia-ala ja Pori Jazz.',
          sv: 'Livlig kuststad känd för sin hamn, energisektor och den berömda Pori Jazz-festivalen.',
        },
      },
      {
        slug: 'Vaasa_Finland',
        name: { en: 'Vaasa', fi: 'Vaasa', sv: 'Vasa' },
        specialty: { en: 'Energy Tech', fi: 'Energiatekniikka', sv: 'Energiteknik' },
        icon: 'Tech',
        narrative: {
          en: 'Bilingual coastal city known for its energy technology sector and strong business environment.',
          fi: 'Kaksikielinen rannikkokaupunki, joka tunnetaan energiatekniikasta ja vahvasta yrityskentästä.',
          sv: 'Tvåspråkig kuststad känd för sin energitekniksektor och starka företagsmiljö.',
        },
      },
      {
        slug: 'Kokkola_Finland',
        name: { en: 'Kokkola', fi: 'Kokkola', sv: 'Karleby' },
        specialty: { en: 'Chemicals & Cleantech', fi: 'Kemia ja cleantech', sv: 'Kemi och cleantech' },
        icon: 'Tech',
        narrative: {
          en: 'Bilingual coastal city known for its chemical industry, port operations, and renewable energy.',
          fi: 'Kaksikielinen rannikkokaupunki: kemianteollisuutta, satama ja uusiutuvaa energiaa.',
          sv: 'Tvåspråkig kuststad känd för sin kemiska industri, hamnverksamhet och förnybar energi.',
        },
      },
      {
        slug: 'Rauma_Finland',
        name: { en: 'Rauma', fi: 'Rauma', sv: 'Raumo' },
        specialty: { en: 'Maritime Heritage', fi: 'Merenkulkuperinne', sv: 'Sjöfartsarv' },
        icon: 'Ship',
        narrative: {
          en: 'UNESCO World Heritage coastal city known for its unique wooden Old Town and maritime industries.',
          fi: 'Unescon maailmanperintökaupunki, jonka puinen vanha kaupunki ja meriteollisuus tunnetaan laajalti.',
          sv: 'UNESCO-världsarvsstad vid kusten känd för sin unika trästad och sjöfartsindustrier.',
        },
      },
      {
        slug: 'Pietarsaari_Finland',
        name: { en: 'Pietarsaari', fi: 'Pietarsaari', sv: 'Jakobstad' },
        specialty: { en: 'Forest Industry & Boatbuilding', fi: 'Metsäteollisuus ja veneenrakennus', sv: 'Skogsindustri och båtbyggeri' },
        icon: 'Tech',
        narrative: {
          en: "Bilingual coastal industrial town built around the Alholmen forest-industry cluster (UPM's pulp mill) and home to renowned yacht builders like Nautor's Swan and Baltic Yachts.",
          fi: "Kaksikielinen rannikon teollisuuskaupunki: Alholman metsäteollisuuskeskittymä UPM:n sellutehtaineen sekä tunnetut veneveistämöt, kuten Nautor's Swan ja Baltic Yachts.",
          sv: "Tvåspråkig industristad vid kusten, byggd kring Alholmens skogsindustrikluster (UPM:s massafabrik) och hem för kända båtvarv som Nautor's Swan och Baltic Yachts.",
        },
      },
      {
        slug: 'Uusikaarlepyy_Finland',
        name: { en: 'Uusikaarlepyy', fi: 'Uusikaarlepyy', sv: 'Nykarleby' },
        specialty: { en: 'Heritage & Small Business', fi: 'Kulttuuriperintö ja pienyritykset', sv: 'Arv och småföretag' },
        icon: 'Building2',
        narrative: {
          en: "One of Finland's smallest towns, birthplace of national poet Zacharias Topelius, with a bilingual small-business economy.",
          fi: 'Yksi Suomen pienimmistä kaupungeista ja kansallisrunoilija Zacharias Topeliuksen syntymäpaikka. Talous nojaa kaksikielisiin pienyrityksiin.',
          sv: 'En av Finlands minsta städer, född ort för nationalskalden Zacharias Topelius, med en tvåspråkig småföretagsekonomi.',
        },
      },
      {
        slug: 'Kristiinankaupunki_Finland',
        name: { en: 'Kristiinankaupunki', fi: 'Kristiinankaupunki', sv: 'Kristinestad' },
        specialty: { en: 'Old Town & Wind Power', fi: 'Vanhakaupunki ja tuulivoima', sv: 'Gamla stan och vindkraft' },
        icon: 'Ship',
        narrative: {
          en: "Coastal town with one of Finland's best-preserved 18th-century wooden old towns, now also a hub for regional wind power.",
          fi: 'Rannikkokaupunki, jonka 1700-luvun puinen vanha kaupunki on Suomen parhaiten säilyneitä. Nykyään myös alueen tuulivoiman keskittymä.',
          sv: 'Kuststad med en av Finlands bäst bevarade trästäder från 1700-talet, numera också ett nav för regional vindkraft.',
        },
      },
      {
        slug: 'Kotka_Finland',
        name: { en: 'Kotka', fi: 'Kotka', sv: 'Kotka' },
        specialty: { en: 'Port & Logistics', fi: 'Satama ja logistiikka', sv: 'Hamn och logistik' },
        icon: 'Ship',
        narrative: {
          en: 'Major port city on the Gulf of Finland, specializing in logistics, maritime industries, and international trade.',
          fi: 'Suomenlahden satamakaupunki, jonka vahvuuksia ovat logistiikka, merenkulku ja kansainvälinen kauppa.',
          sv: 'Stor hamnstad vid Finska viken, specialiserad på logistik, sjöfartsindustrier och internationell handel.',
        },
      },
      {
        slug: 'Hanko_Finland',
        name: { en: 'Hanko', fi: 'Hanko', sv: 'Hangö' },
        specialty: { en: 'Maritime & Tourism', fi: 'Merenkulku ja matkailu', sv: 'Sjöfart och turism' },
        icon: 'Ship',
        narrative: {
          en: 'Southernmost city in Finland, known for its harbor, seaside location, and growing tourism sector.',
          fi: 'Suomen eteläisin kaupunki: satama, meri joka puolella ja kasvava matkailuala.',
          sv: 'Finlands sydligaste stad, känd för sin hamn, kustläge och växande turismsektor.',
        },
      },
      {
        slug: 'Salo_Finland',
        name: { en: 'Salo', fi: 'Salo', sv: 'Salo' },
        specialty: { en: 'Industrial Tech', fi: 'Teollisuusteknologia', sv: 'Industriteknik' },
        icon: 'Tech',
        narrative: {
          en: 'Southwest Finland technology town reinventing itself around IoT, battery technology, and smart manufacturing.',
          fi: 'Varsinais-Suomen teknologiakaupunki, joka rakentaa uutta IoT:n, akkuteknologian ja älykkään valmistuksen varaan.',
          sv: 'Teknikstad i Egentliga Finland som förnyar sig kring IoT, batteriteknik och smart tillverkning.',
        },
      },
    ],
  },
  {
    id: 'central',
    title: { en: 'Tech Corridor', fi: 'Teknologiaväylä', sv: 'Teknikkorridor' },
    description: {
      en: 'Student-driven innovation in Tampere and Jyväskylä, with strong manufacturing, gaming, and university research.',
      fi: 'Tampereen ja Jyväskylän opiskelijakaupungit: valmistavaa teollisuutta, pelialaa ja yliopistotutkimusta.',
      sv: 'Studentedriven innovation i Tammerfors och Jyväskylä med stark tillverkning, spel och universitetsforskning.',
    },
    cities: [
      {
        slug: 'Tampere_Finland',
        name: { en: 'Tampere', fi: 'Tampere', sv: 'Tammerfors' },
        specialty: { en: 'Manufacturing & Gaming', fi: 'Valmistus ja peliteollisuus', sv: 'Tillverkning och spelteknologi' },
        icon: 'Gaming',
        narrative: {
          en: 'Student-driven innovation hub with strong manufacturing and gaming sectors, home to successful game studios.',
          fi: 'Opiskelijakaupunki, jossa on vahva valmistava teollisuus ja peliala sekä useita menestyneitä pelistudioita.',
          sv: 'Studentedriven innovationsnav med stark tillverkning och spelindustri, hem för framgångsrika spelstudios.',
        },
      },
      {
        slug: 'Jyväskylä_Finland',
        name: { en: 'Jyväskylä', fi: 'Jyväskylä', sv: 'Jyväskylä' },
        specialty: { en: 'Education & Tech', fi: 'Koulutus ja tekniikka', sv: 'Utbildning och teknik' },
        icon: 'Education',
        narrative: {
          en: 'University city with strong tech education and research programs, fostering innovation and entrepreneurship.',
          fi: 'Yliopistokaupunki, jossa tekninen koulutus ja tutkimus ruokkivat innovaatioita ja yrittäjyyttä.',
          sv: 'Universitetsstad med stark teknisk utbildning och forskningsprogram, främjar innovation och entreprenörskap.',
        },
      },
      {
        slug: 'Lahti_Finland',
        name: { en: 'Lahti', fi: 'Lahti', sv: 'Lahtis' },
        specialty: { en: 'Sports & Design', fi: 'Urheilu ja design', sv: 'Sport och design' },
        icon: 'Sports',
        narrative: {
          en: 'Regional center known for sports, design, and green technology with a growing business ecosystem.',
          fi: 'Aluekeskus, joka tunnetaan urheilusta, designista ja ympäristöteknologiasta. Yrityskenttä kasvaa.',
          sv: 'Regionalt centrum känt för sport, design och grön teknik med växande affärsekosystem.',
        },
      },
      {
        slug: 'Hämeenlinna_Finland',
        name: { en: 'Hämeenlinna', fi: 'Hämeenlinna', sv: 'Tavastehus' },
        specialty: { en: 'Historic & Industrial', fi: 'Historia ja teollisuus', sv: 'Historisk och industriell' },
        icon: 'Building2',
        narrative: {
          en: 'Historic city known for its medieval castle and as a key industrial center between Helsinki and Tampere.',
          fi: 'Historiallinen linnakaupunki ja teollisuuskeskus Helsingin ja Tampereen välillä.',
          sv: 'Historisk stad känd för sitt medeltida slott och som ett viktigt industricentrum mellan Helsingfors och Tammerfors.',
        },
      },
      {
        slug: 'Kouvola_Finland',
        name: { en: 'Kouvola', fi: 'Kouvola', sv: 'Kouvola' },
        specialty: { en: 'Transport & Logistics', fi: 'Kuljetus ja logistiikka', sv: 'Transport och logistik' },
        icon: 'Building2',
        narrative: {
          en: 'Key transport and logistics hub in southeastern Finland, connecting major cities and industrial areas.',
          fi: 'Kaakkois-Suomen kuljetus- ja logistiikkakeskus suurten kaupunkien ja teollisuusalueiden välissä.',
          sv: 'Viktigt transport- och logistiknav i sydöstra Finland, kopplar samman större städer och industrisområden.',
        },
      },
    ],
  },
  {
    id: 'east',
    title: { en: 'Eastern Centers', fi: 'Itäiset keskukset', sv: 'Östra centra' },
    description: {
      en: 'Regional hubs in eastern Finland with strong university research, healthcare technology, and natural resources expertise.',
      fi: 'Itä-Suomen aluekeskukset: yliopistotutkimusta, terveysteknologiaa ja luonnonvara-alaa.',
      sv: 'Regionala nav i östra Finland med stark universitetsforskning, hälsoteknik och naturresursexpertis.',
    },
    cities: [
      {
        slug: 'Kuopio_Finland',
        name: { en: 'Kuopio', fi: 'Kuopio', sv: 'Kuopio' },
        specialty: { en: 'Health Tech', fi: 'Terveystekniikka', sv: 'Hälsoteknik' },
        icon: 'Research',
        narrative: {
          en: 'Largest city in eastern Finland and significant center for health technology and wellbeing industries.',
          fi: 'Itä-Suomen suurin kaupunki sekä terveysteknologian ja hyvinvointialan keskus.',
          sv: 'Största staden i östra Finland och betydande centrum för hälsoteknik och välfärdsindustrier.',
        },
      },
      {
        slug: 'Joensuu_Finland',
        name: { en: 'Joensuu', fi: 'Joensuu', sv: 'Joensuu' },
        specialty: { en: 'Forestry & Research', fi: 'Metsäteollisuus ja tutkimus', sv: 'Skogsindustri och forskning' },
        icon: 'Research',
        narrative: {
          en: 'Vibrant capital of North Karelia, known for its university, forestry expertise, and high quality of life.',
          fi: 'Pohjois-Karjalan maakuntakeskus, joka tunnetaan yliopistostaan, metsäosaamisestaan ja hyvästä elämänlaadusta.',
          sv: 'Livliga huvudstaden i Norra Karelen, känd för sitt universitet, skogsexpertis och höga livskvalitet.',
        },
      },
      {
        slug: 'Lappeenranta_Finland',
        name: { en: 'Lappeenranta', fi: 'Lappeenranta', sv: 'Villmanstrand' },
        specialty: { en: 'Clean Tech', fi: 'Cleantech', sv: 'Ren teknik' },
        icon: 'Research',
        narrative: {
          en: 'Charming lakeside city near the Russian border, known for its university, clean technology, and tourism.',
          fi: 'Järvikaupunki lähellä Venäjän rajaa: yliopisto, cleantech ja matkailu.',
          sv: 'Charmig stad vid sjön nära den ryska gränsen, känd för sitt universitet, ren teknologi och turism.',
        },
      },
      {
        slug: 'Mikkeli_Finland',
        name: { en: 'Mikkeli', fi: 'Mikkeli', sv: 'S:t Michel' },
        specialty: { en: 'Defense Tech', fi: 'Puolustustekniikka', sv: 'Försvarsteknik' },
        icon: 'Research',
        narrative: {
          en: 'Charming lakeside city known for its military heritage and growing technology sector.',
          fi: 'Järvikaupunki, joka tunnetaan sotahistoriastaan ja kasvavasta teknologia-alastaan.',
          sv: 'Charmig stad vid sjön känd för sitt militära arv och växande tekniksektor.',
        },
      },
      {
        slug: 'Kajaani_Finland',
        name: { en: 'Kajaani', fi: 'Kajaani', sv: 'Kajana' },
        specialty: { en: 'Supercomputing & Research', fi: 'Supertietokoneet ja tutkimus', sv: 'Superdatorer och forskning' },
        icon: 'Research',
        narrative: {
          en: "Capital of the Kainuu region, once a paper-mill town, now home to CSC's LUMI supercomputer and a growing data-center industry.",
          fi: 'Kainuun maakuntakeskus ja entinen paperitehdaskaupunki, jossa on nyt CSC:n LUMI-supertietokone ja kasvava konesaliala.',
          sv: 'Huvudstad i Kajanaland, en gång en pappersbruksstad, numera hem för CSC:s superdator LUMI och en växande datacenterindustri.',
        },
      },
    ],
  },
  {
    id: 'north',
    title: { en: 'Northern Tech', fi: 'Pohjoinen tekniikka', sv: 'Norra tekniken' },
    description: {
      en: 'Telecommunications and 6G technology hub, with strong university research and growing health tech sector.',
      fi: 'Tietoliikenteen ja 6G-teknologian keskus, jossa on vahva yliopistotutkimus ja kasvava terveysteknologia-ala.',
      sv: 'Telekommunikations- och 6G-teknologinav med stark universitetsforskning och växande hälsotekniksektor.',
    },
    cities: [
      {
        slug: 'Oulu_Finland',
        name: { en: 'Oulu', fi: 'Oulu', sv: 'Uleåborg' },
        specialty: { en: '6G & Health Tech', fi: '6G ja terveystekniikka', sv: '6G och hälsoteknik' },
        icon: 'Research',
        narrative: {
          en: 'Telecommunications and 6G technology hub with strong university research and growing health tech sector.',
          fi: 'Tietoliikenteen ja 6G-teknologian keskus, jossa on vahva yliopistotutkimus ja kasvava terveysteknologia-ala.',
          sv: 'Telekommunikations- och 6G-teknologinav med stark universitetsforskning och växande hälsotekniksektor.',
        },
      },
      {
        slug: 'Rovaniemi_Finland',
        name: { en: 'Rovaniemi', fi: 'Rovaniemi', sv: 'Rovaniemi' },
        specialty: { en: 'Arctic Research', fi: 'Arktinen tutkimus', sv: 'Arktisk forskning' },
        icon: 'Research',
        narrative: {
          en: 'Official hometown of Santa Claus and capital of Finnish Lapland, offering opportunities in Arctic research and mining technology.',
          fi: 'Joulupukin virallinen kotikaupunki ja Lapin pääkaupunki. Töitä arktisessa tutkimuksessa ja kaivostekniikassa.',
          sv: 'Jultomtens officiella hemstad och huvudstaden i Finska Lappland, erbjuder möjligheter inom arktisk forskning och gruvteknik.',
        },
      },
      {
        slug: 'Seinäjoki_Finland',
        name: { en: 'Seinäjoki', fi: 'Seinäjoki', sv: 'Seinäjoki' },
        specialty: { en: 'Agriculture & Tech', fi: 'Maatalous ja tekniikka', sv: 'Jordbruk och teknik' },
        icon: 'Research',
        narrative: {
          en: 'Modern regional center in South Ostrobothnia, known for agriculture, food processing, and growing technology sectors.',
          fi: 'Etelä-Pohjanmaan maakuntakeskus: maataloutta, elintarviketeollisuutta ja kasvavia teknologia-aloja.',
          sv: 'Modernt regionalt centrum i Södra Österbotten, känt för jordbruk, livsmedelsförädling och växande tekniksektorer.',
        },
      },
      {
        slug: 'Kemi_Finland',
        name: { en: 'Kemi', fi: 'Kemi', sv: 'Kemi' },
        specialty: { en: 'Port & Industry', fi: 'Satama ja teollisuus', sv: 'Hamn och industri' },
        icon: 'Ship',
        narrative: {
          en: 'Northern port city known for its pulp and paper industry, port operations, and industrial heritage.',
          fi: 'Pohjoinen satamakaupunki, jonka tunnetuin ala on sellu- ja paperiteollisuus. Vahva teollinen perinne.',
          sv: 'Norra hamnstad känd för sin mass- och pappersindustri, hamnverksamhet och industriella arv.',
        },
      },
    ],
  },
  {
    id: 'south',
    title: { en: 'Southern Centers', fi: 'Eteläiset keskukset', sv: 'Södra centra' },
    description: {
      en: 'Historic towns and emerging centers in southern Finland with strong cultural heritage and growing business scenes.',
      fi: 'Etelä-Suomen historialliset kaupungit ja kasvavat keskukset, joissa on vahva kulttuuriperinne ja laajeneva yrityskenttä.',
      sv: 'Historiska städer och framväxande centra i södra Finland med stark kulturarv och expanderande affärsmiljöer.',
    },
    cities: [
      {
        slug: 'Porvoo_Finland',
        name: { en: 'Porvoo', fi: 'Porvoo', sv: 'Borgå' },
        specialty: { en: 'Historic & Energy', fi: 'Historia ja energia', sv: 'Historisk och energi' },
        icon: 'Building2',
        narrative: {
          en: 'Charming historic town near Helsinki, known for its picturesque old town and strong oil refining industry.',
          fi: 'Historiallinen kaupunki Helsingin lähellä, joka tunnetaan vanhasta kaupungistaan ja öljynjalostuksesta.',
          sv: 'Charmig historisk stad nära Helsingfors, känd för sin pittoreska gamla stad och starka oljeraffinaderier.',
        },
      },
      {
        slug: 'Tammisaari_Finland',
        name: { en: 'Tammisaari', fi: 'Tammisaari', sv: 'Ekenäs' },
        specialty: { en: 'Historic Old Town', fi: 'Historiallinen vanhakaupunki', sv: 'Historisk gamla stan' },
        icon: 'Building2',
        narrative: {
          en: 'Swedish-speaking archipelago town known for its well-preserved wooden old town and boating tourism.',
          fi: 'Ruotsinkielinen saaristokaupunki, joka tunnetaan hyvin säilyneestä puisesta vanhasta kaupungistaan ja veneilymatkailusta.',
          sv: 'Svenskspråkig skärgårdsstad känd för sin välbevarade trästad och båtturism.',
        },
      },
      {
        slug: 'Hyvinkää_Finland',
        name: { en: 'Hyvinkää', fi: 'Hyvinkää', sv: 'Hyvinge' },
        specialty: { en: 'Rail & Logistics', fi: 'Rautatiet ja logistiikka', sv: 'Järnväg och logistik' },
        icon: 'Building2',
        narrative: {
          en: "Railway town on Helsinki's main commuter line, historically home to VR's rail workshops and a growing logistics and manufacturing base.",
          fi: 'Rautatiekaupunki pääradan varrella. VR:n konepajan perinne sekä kasvava logistiikka ja valmistava teollisuus.',
          sv: 'Järnvägsstad vid Helsingfors huvudbana, historiskt hem för VR:s järnvägsverkstäder och en växande logistik- och tillverkningsbas.',
        },
      },
      {
        slug: 'Nurmijärvi_Finland',
        name: { en: 'Nurmijärvi', fi: 'Nurmijärvi', sv: 'Nurmijärvi' },
        specialty: { en: 'Commuter Belt & Heritage', fi: 'Pendelöintialue ja kulttuuriperintö', sv: 'Pendlingsregion och kulturarv' },
        icon: 'Building2',
        narrative: {
          en: 'Fast-growing Helsinki commuter municipality, birthplace of national author Aleksis Kivi and home to a diversified suburban economy.',
          fi: 'Nopeasti kasvava pendelöintikunta Helsingin lähellä ja kansalliskirjailija Aleksis Kiven syntymäpaikka. Elinkeinot ovat monipuoliset.',
          sv: 'Snabbt växande pendlingskommun i Helsingforsregionen, född ort för nationalförfattaren Aleksis Kivi och hem för en diversifierad förortsekonomi.',
        },
      },
      {
        slug: 'Lohja_Finland',
        name: { en: 'Lohja', fi: 'Lohja', sv: 'Lojo' },
        specialty: { en: 'Limestone Heritage & Growth', fi: 'Kalkkiteollisuuden perintö ja kasvu', sv: 'Kalkstensarv och tillväxt' },
        icon: 'Building2',
        narrative: {
          en: 'Lake-district town built on a historic limestone and cement industry, now a fast-growing part of the Helsinki region.',
          fi: 'Järvikaupunki, joka kasvoi kalkki- ja sementtiteollisuuden varassa ja on nyt nopeasti kasvava osa Helsingin seutua.',
          sv: 'Stad i sjölandskapet byggd på en historisk kalksten- och cementindustri, numera en snabbt växande del av Helsingforsregionen.',
        },
      },
    ],
  },
];

/**
 * Get city clusters for a specific locale
 */
export function getCityClustersForLocale(locale: SupportedLocale): CityCluster[] {
  return FINLAND_CITY_CLUSTERS.map(cluster => ({
    ...cluster,
    title: cluster.title[locale],
    description: cluster.description[locale],
    cities: cluster.cities.map(city => ({
      ...city,
      name: city.name[locale],
      specialty: city.specialty ? city.specialty[locale] : undefined,
      narrative: city.narrative[locale],
    })),
  }));
}

/**
 * Get all city slugs from clusters
 */
export function getAllCitySlugs(): string[] {
  return FINLAND_CITY_CLUSTERS.flatMap(cluster => cluster.cities.map(city => city.slug));
}

/** ASCII-fold, e.g. "Jyväskylä" -> "Jyvaskyla". Same technique as toLocationSlug. */
function asciiFoldCityName(name: string): string {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * ASCII-folded city name -> its real (diacritic) spelling, built lazily from this file's own
 * curated FINLAND_CITY_CLUSTERS list (en/fi/sv display names), e.g. "jyvaskyla" -> "jyväskylä",
 * "hameenlinna" -> "hämeenlinna". Deliberately scoped to this hand-curated ~24-city list rather
 * than the much larger GeoNames gazetteer (utils/map/finland-geonames-lookup.ts): that dataset's
 * raw alternate-name aliases include noisy/incorrect transliterations (e.g. "helsînkî", "tamperė")
 * which would "restore" an already-correct ASCII name into garbage - see the note left in that
 * file. Only names that actually contain a diacritic are indexed.
 */
let asciiFoldToDiacriticCityNameIndex: Map<string, string> | null = null;

function buildAsciiFoldToDiacriticCityNameIndex(): Map<string, string> {
  const index = new Map<string, string>();
  for (const cluster of FINLAND_CITY_CLUSTERS) {
    for (const city of cluster.cities) {
      for (const name of [city.name.en, city.name.fi, city.name.sv]) {
        if (!name) continue;
        const lower = name.toLowerCase();
        const folded = asciiFoldCityName(lower);
        if (folded === lower) continue; // no diacritic, nothing to restore
        if (!index.has(folded)) index.set(folded, lower);
      }
    }
  }
  return index;
}

/**
 * Restores the diacritics an ASCII-folded slug word lost (TEC-472's toLocationSlug ASCII-fold),
 * e.g. `restoreDiacriticsForAsciiFoldedFinnishCityName("jyvaskyla")` -> `"jyväskylä"`. Returns
 * null when `name` isn't one of this app's curated Finnish hub cities with a diacritic form - it
 * may already be accented, may not be a curated city at all, or may genuinely have no diacritics.
 * Callers should fall back to the original input in that case (see decodeLocationSlug).
 */
export function restoreDiacriticsForAsciiFoldedFinnishCityName(name: string): string | null {
  if (!asciiFoldToDiacriticCityNameIndex) {
    asciiFoldToDiacriticCityNameIndex = buildAsciiFoldToDiacriticCityNameIndex();
  }
  return asciiFoldToDiacriticCityNameIndex.get(name.trim().toLowerCase()) ?? null;
}

/**
 * Get major hub city slugs for overview (Helsinki metro, Tampere, Turku, Oulu)
 */
export function getMajorHubSlugs(): string[] {
  return [
    'Helsinki_Finland',
    'Espoo_Finland',
    'Vantaa_Finland',
    'Tampere_Finland',
    'Turku_Finland',
    'Oulu_Finland',
  ];
}

/**
 * Get city data by slug
 */
export function getCityDataBySlug(slug: string, locale: SupportedLocale): CityData | null {
  // toLocationSlug (ASCII-fold + lowercase, TEC-472), not a plain .toLowerCase(): `slug` here is
  // routinely a route param that has already gone through toLocationSlug ("jyvaskyla_finland"),
  // while `c.slug` in FINLAND_CITY_CLUSTERS is still the literal diacritic spelling
  // ("Jyväskylä_Finland") - a lowercase-only compare would never match those five cities
  // (Jyväskylä, Hämeenlinna, Seinäjoki, Nurmijärvi, Hyvinkää).
  const slugFolded = toLocationSlug(slug);
  for (const cluster of FINLAND_CITY_CLUSTERS) {
    const city = cluster.cities.find(c => toLocationSlug(c.slug) === slugFolded);
    if (city) {
      return {
        ...city,
        name: city.name[locale],
        specialty: city.specialty ? city.specialty[locale] : undefined,
        narrative: city.narrative[locale],
      };
    }
  }
  return null;
}

/**
 * True when a raw (possibly percent-encoded) location URL segment matches one of the curated
 * Finland city hub slugs (e.g. `Helsinki_Finland`). Used to gate `/jobs/location/:slug` pages so
 * arbitrary or wrong-country slugs (TEC-467 Bug 2) don't render as if they were real hub pages.
 */
export function isKnownFinlandLocationSlug(rawSlug: string): boolean {
  if (!rawSlug) return false;
  let decoded = rawSlug;
  try {
    decoded = decodeURIComponent(rawSlug);
  } catch {
    // ignore decode issues; fall back to comparing the raw value
  }
  // toLocationSlug (ASCII-fold + lowercase, TEC-472), not a plain .toLowerCase(): see the
  // identical note in getCityDataBySlug above.
  const decodedFolded = toLocationSlug(decoded);
  return getAllCitySlugs().some(slug => toLocationSlug(slug) === decodedFolded);
}

/**
 * Curated hub slug ("Tampere_Finland") for a city display name in any locale ("Tampere",
 * "Tammerfors, Finland", "Tampere, Suomi"), or null when the city is not a curated hub.
 * Job records carry the city in the page locale, so an English-slug check alone misses fi/sv.
 */
export function findFinlandHubSlugByCityName(cityName: string | null | undefined): string | null {
  const key = (cityName ?? '').split(',')[0].trim().toLowerCase();
  if (!key) return null;
  for (const cluster of FINLAND_CITY_CLUSTERS) {
    for (const city of cluster.cities) {
      if (city.name.en.toLowerCase() === key || city.name.fi.toLowerCase() === key || city.name.sv.toLowerCase() === key) {
        return city.slug;
      }
    }
  }
  return null;
}
